// Medir sobre la foto de una cámara fija: toda la geometría, en el navegador, sin dependencias.
//
// MÉTODO (ver medirValidacion.test.js para los errores medidos):
//   Se ajusta un modelo de cámara completo por cuadrados mínimos (Levenberg-Marquardt) a las referencias que
//   marca el usuario una sola vez por cámara:
//     - cámara pinhole: focal f (píxeles cuadrados, centro óptico en el centro de la foto) + pose (rotación y
//       posición respecto del piso, 6 parámetros);
//     - distorsión radial de un parámetro (modelo de división de Fitzgibbon): las cámaras de obra son gran
//       angular y curvan la imagen en los bordes; sin corregirlo los errores lejos del centro pasan del 10 %.
//   Referencias:
//     - un rectángulo sobre el piso con sus dos lados conocidos (fija el plano del piso, la escala y la focal:
//       es la calibración plana de Zhang con una sola vista);
//     - líneas que en la realidad son rectas (3+ puntos cada una): fijan la distorsión (plumb-line);
//     - opcional: referencias verticales de alto conocido (base + punta): fijan mejor la inclinación y la focal,
//       que es lo que más pesa en las alturas;
//     - opcional: más segmentos de piso de largo conocido.
//   Con el modelo: distancia/área = se proyecta cada clic sobre el plano del piso (Z = 0); altura = se busca la
//   altura Z del punto que está en la vertical de la base y cae sobre la punta marcada.
//   Esto es equivalente a la metrología de una vista de Criminisi (punto de fuga vertical + horizonte), pero con
//   un modelo físico de 7-8 parámetros que se ajusta a todas las referencias juntas: es más estable con pocas
//   referencias cortas (con una sola vertical Criminisi no puede estimar el punto de fuga) y corrige distorsión.
//
// MARGEN DE ERROR (Monte Carlo): se repite la calibración con los clics movidos al azar según su precisión
// (σ ≈ 2,5 px en 4K con zoom, más si se marcó sin zoom) y con las medidas de referencia movidas según su
// tolerancia (1 % con cinta, 4 % si es un tamaño típico). Cada medida se recalcula con todas esas calibraciones
// y con sus propios clics movidos: el ± es el intervalo del 95 %. Crece solo con la distancia a la cámara y a
// las referencias, que es lo que pasa de verdad.
//
// Coordenadas: los puntos se guardan en píxeles de la foto original (sin rotar), [u, v] o [u, v, σ].
// Mundo: metros, el piso es Z = 0 y Z apunta hacia arriba.

export const SIGMA_4K = 2.5; // px de la foto 4K: clic con zoom
export const TOL_EXACTA = 0.01; // medida con cinta
export const TOL_APROX = 0.04; // tamaño típico (contenedor, puerta, camión)
export const MUESTRAS = 80;
// Curva del lente de las cámaras de Lisual (todas Reolink Go PT Ultra 4K, el mismo lente), medida UNA vez para
// todas: 22 cámaras / 43 fotos reales (OMI, BD, Murato, Nativo, ENNE, Tecnova, Boschetto…), tramos rectos largos
// detectados solos (bordes de edificios, losas, muros, vías, horizonte) y revisados a ojo uno por uno, ajuste
// plumb-line conjunto con el modelo de división de abajo. Cámaras con muchos tramos: -0,33 a -0,44 (BD -0,37,
// OMI SL02 -0,44, SL03 -0,38, ENNE -0,29, galpón de LOTE 25 -0,41); conjunto -0,34/-0,37; bootstrap por cámara σ ≈ 0,02.
// Las que dieron otra cosa tenían pocos tramos útiles cerca del borde: enderezadas con este valor sus bordes
// quedan rectos igual. Entra al ajuste como previa (con su σ, que también entra al ±), así que el cliente ya no
// marca "líneas rectas"; si alguna vez se marcan (calibraciones viejas), mandan las líneas.
export const LENTE_REOLINK = { k: -0.36, sigma: 0.04 };
// grados que puede apartarse una cosa vertical de la vertical del modelo antes de avisar "piso en pendiente"
export const UMBRAL_PENDIENTE = 4;
// Previa de la focal (log): las cámaras de obra son gran angular (~100°). Sólo pesa si las referencias son chicas o
// casi de frente; con un rectángulo grande manda el rectángulo. 0,3 ≈ ±35 %.
export const SIGMA_FOCAL = 0.3;
// Previa del giro de la cámara (roll): las cámaras de obra se montan derechas, con el horizonte horizontal. Con un
// rectángulo chico el ajuste tiene dos soluciones que encajan igual de bien (la ambigüedad de pose de un plano chico)
// y la falsa queda torcida 40-60°: esta previa la descarta. σ ≈ 8°.
export const SIGMA_ROLL = Math.sin((8 * Math.PI) / 180);

// ---------- álgebra chica ----------
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a) => Math.hypot(a[0], a[1], a[2]);
const mv = (M, v) => [dot(M[0], v), dot(M[1], v), dot(M[2], v)];
const mtv = (M, v) => [
  M[0][0] * v[0] + M[1][0] * v[1] + M[2][0] * v[2],
  M[0][1] * v[0] + M[1][1] * v[1] + M[2][1] * v[2],
  M[0][2] * v[0] + M[1][2] * v[1] + M[2][2] * v[2],
];

export function rodrigues(w) {
  const th = norm(w);
  if (th < 1e-12) return [[1, -w[2], w[1]], [w[2], 1, -w[0]], [-w[1], w[0], 1]];
  const k = [w[0] / th, w[1] / th, w[2] / th];
  const c = Math.cos(th);
  const s = Math.sin(th);
  const v = 1 - c;
  return [
    [c + k[0] * k[0] * v, k[0] * k[1] * v - k[2] * s, k[0] * k[2] * v + k[1] * s],
    [k[1] * k[0] * v + k[2] * s, c + k[1] * k[1] * v, k[1] * k[2] * v - k[0] * s],
    [k[2] * k[0] * v - k[1] * s, k[2] * k[1] * v + k[0] * s, c + k[2] * k[2] * v],
  ];
}

export function rotToVec(R) {
  const tr = R[0][0] + R[1][1] + R[2][2];
  const cos = Math.min(1, Math.max(-1, (tr - 1) / 2));
  const th = Math.acos(cos);
  if (th < 1e-9) return [0, 0, 0];
  if (Math.PI - th < 1e-4) {
    // cerca de 180°: eje desde R + I
    const B = [[R[0][0] + 1, R[0][1], R[0][2]], [R[1][0], R[1][1] + 1, R[1][2]], [R[2][0], R[2][1], R[2][2] + 1]];
    const cols = [0, 1, 2].map((j) => [B[0][j], B[1][j], B[2][j]]);
    const c = cols.reduce((a, b) => (norm(b) > norm(a) ? b : a));
    const n = norm(c);
    return [(c[0] / n) * th, (c[1] / n) * th, (c[2] / n) * th];
  }
  const s = 2 * Math.sin(th);
  return [((R[2][1] - R[1][2]) / s) * th, ((R[0][2] - R[2][0]) / s) * th, ((R[1][0] - R[0][1]) / s) * th];
}

export function solve(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    if (Math.abs(M[p][c]) < 1e-300) return null;
    [M[c], M[p]] = [M[p], M[c]];
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      if (f !== 0) for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

// Azar con semilla (los ± tienen que dar lo mismo cada vez que se abre la pantalla).
export function rng(seed = 1) {
  let a = seed >>> 0;
  const u = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const g = () => {
    let x = 0;
    while (x === 0) x = u();
    return Math.sqrt(-2 * Math.log(x)) * Math.cos(2 * Math.PI * u());
  };
  return { u, g };
}

const hashPts = (pts) => {
  let h = 2166136261;
  for (const p of pts) for (const x of [p[0], p[1]]) { h ^= Math.round(x * 10); h = Math.imul(h, 16777619); }
  return h >>> 0;
};

// ---------- modelo de cámara ----------
export const focalPorDefecto = (w) => (w / 2) / Math.tan((100 * Math.PI) / 360); // ~100° de ángulo horizontal

export function crearModelo({ w, h, f, k = 0, rv, t }) {
  return { w, h, f, k, rv, t, R: rodrigues(rv), cx: w / 2, cy: h / 2, rd: Math.hypot(w, h) / 2 };
}

/** píxel de la foto -> píxel sin distorsión (null si cae fuera de la zona válida del modelo) */
export function sinDistorsion(m, p) {
  const dx = (p[0] - m.cx) / m.rd;
  const dy = (p[1] - m.cy) / m.rd;
  const den = 1 + m.k * (dx * dx + dy * dy);
  if (den < 0.05) return null;
  return [m.cx + (dx / den) * m.rd, m.cy + (dy / den) * m.rd];
}

/** píxel sin distorsión -> píxel de la foto */
export function conDistorsion(m, q) {
  if (!m.k) return [q[0], q[1]];
  const dx = (q[0] - m.cx) / m.rd;
  const dy = (q[1] - m.cy) / m.rd;
  const ru = Math.hypot(dx, dy);
  if (ru < 1e-12) return [q[0], q[1]];
  const disc = 1 - 4 * m.k * ru * ru;
  if (disc < 0) return null;
  const rdist = (1 - Math.sqrt(disc)) / (2 * m.k * ru);
  const s = rdist / ru;
  return [m.cx + dx * s * m.rd, m.cy + dy * s * m.rd];
}

/** punto del mundo (m) -> píxel de la foto, o null si queda detrás de la cámara */
export function proyectar(m, X) {
  const c = mv(m.R, X);
  const z = c[2] + m.t[2];
  if (z <= 1e-6) return null;
  return conDistorsion(m, [m.cx + (m.f * (c[0] + m.t[0])) / z, m.cy + (m.f * (c[1] + m.t[1])) / z]);
}

export const centroCamara = (m) => {
  const c = mtv(m.R, m.t);
  return [-c[0], -c[1], -c[2]];
};

const rayo = (m, p) => {
  const q = sinDistorsion(m, p);
  if (!q) return null;
  return mtv(m.R, [(q[0] - m.cx) / m.f, (q[1] - m.cy) / m.f, 1]);
};

/** píxel -> punto del piso [X, Y] en metros, o null si el clic está por encima del horizonte */
export function alPiso(m, p) {
  const d = rayo(m, p);
  if (!d) return null;
  const C = centroCamara(m);
  if (Math.abs(d[2]) < 1e-12) return null;
  const s = -C[2] / d[2];
  if (s <= 0) return null;
  return [C[0] + s * d[0], C[1] + s * d[1]];
}

/** altura (m) de la punta `top` sobre el punto del piso que está en `base` */
export function alturaSobre(m, base, top) {
  const X = alPiso(m, base);
  const q = sinDistorsion(m, top);
  if (!X || !q) return null;
  const K = (v) => [m.f * v[0] + m.cx * v[2], m.f * v[1] + m.cy * v[2], v[2]];
  const c = mv(m.R, [X[0], X[1], 0]);
  const a = K([c[0] + m.t[0], c[1] + m.t[1], c[2] + m.t[2]]);
  const b = K([m.R[0][2], m.R[1][2], m.R[2][2]]);
  const p = [q[0], q[1], 1];
  const bp = cross(b, p);
  const ap = cross(a, p);
  const den = dot(bp, bp);
  if (den < 1e-18) return null;
  return -dot(bp, ap) / den;
}

// ---------- medidas ----------
export const TIPOS = {
  distancia: { min: 2, max: 40, unidad: 'm', nombre: 'Distancia' },
  area: { min: 3, max: 40, unidad: 'm²', nombre: 'Superficie' },
  altura: { min: 2, max: 2, unidad: 'm', nombre: 'Altura' },
  volumen: { min: 3, max: 40, unidad: 'm³', nombre: 'Volumen' },
  pared: { min: 2, max: 40, unidad: 'm', nombre: 'En pared' },
};

// Volumen aproximado = superficie de la base × alto × factor de forma (y la duda de la forma entra al ±).
export const FORMAS = {
  pila: { factor: 1 / 3, duda: 0.2, nombre: 'Montaña' },
  alargada: { factor: 1 / 2, duda: 0.15, nombre: 'Montaña alargada' },
  caja: { factor: 1, duda: 0.05, nombre: 'Caja o contenedor' },
  pozo: { factor: 1, duda: 0.1, nombre: 'Pozo' },
};

/** punto en altura: la base apoyada en el piso y la punta arriba -> [X, Y, Z] */
export function puntoAlto(m, base, punta) {
  const X = alPiso(m, base);
  if (!X) return null;
  const z = alturaSobre(m, base, punta);
  return z == null ? null : [X[0], X[1], z];
}

/** píxel -> punto [X, Y, Z] sobre la pared vertical que pasa por los puntos del piso A y B (o null) */
export function alPared(m, A, B, p) {
  const d = rayo(m, p);
  if (!d) return null;
  const C = centroCamara(m);
  const n = [-(B[1] - A[1]), B[0] - A[0], 0];
  const den = dot(n, d);
  if (Math.abs(den) < 1e-12) return null;
  const s = dot(n, [A[0] - C[0], A[1] - C[1], -C[2]]) / den;
  if (s <= 0) return null;
  return [C[0] + s * d[0], C[1] + s * d[1], C[2] + s * d[2]];
}

const areaPoligono = (P) => {
  let A = 0;
  for (let i = 0; i < P.length; i++) { const a = P[i]; const b = P[(i + 1) % P.length]; A += a[0] * b[1] - b[0] * a[1]; }
  return Math.abs(A) / 2;
};
const centroide = (P) => [P.reduce((s, q) => s + q[0], 0) / P.length, P.reduce((s, q) => s + q[1], 0) / P.length];
const largo3 = (Q) => { let L = 0; for (let i = 1; i < Q.length; i++) L += Math.hypot(Q[i][0] - Q[i - 1][0], Q[i][1] - Q[i - 1][1], (Q[i][2] || 0) - (Q[i - 1][2] || 0)); return L; };

/** los puntos 3D de una medida (null si alguno no se puede ubicar). extra: { bases, linea, cima, forma } */
export function puntos3D(m, tipo, pts, extra = {}) {
  if (tipo === 'pared') {
    const [a, b] = extra.linea || [];
    const A = a && alPiso(m, a);
    const B = b && alPiso(m, b);
    if (!A || !B || Math.hypot(A[0] - B[0], A[1] - B[1]) < 1e-6) return null;
    const Q = pts.map((p) => alPared(m, A, B, p));
    return Q.some((x) => !x) ? null : Q;
  }
  const bases = extra.bases || [];
  const Q = pts.map((p, i) => {
    if (tipo === 'distancia' && bases[i]) return puntoAlto(m, bases[i], p);
    const X = alPiso(m, p);
    return X ? [X[0], X[1], 0] : null;
  });
  return Q.some((x) => !x) ? null : Q;
}

/** alto (con signo: negativo = pozo) de la cima sobre el centro de la base del volumen */
export function altoVolumen(m, base3, cima) {
  const c = centroide(base3);
  const q = proyectar(m, [c[0], c[1], 0]);
  if (!q) return null;
  return alturaSobre(m, q, cima);
}

/** valor de una medida con un modelo (null si algún punto no se puede ubicar) */
export function medir(m, tipo, pts, extra = {}) {
  if (tipo === 'altura') {
    if (pts.length < 2) return null;
    return alturaSobre(m, pts[0], pts[1]);
  }
  const Q = puntos3D(m, tipo, pts, extra);
  if (!Q) return null;
  if (tipo === 'distancia' || tipo === 'pared') return Q.length < 2 ? null : largo3(Q);
  if (tipo === 'area') return Q.length < 3 ? null : areaPoligono(Q);
  if (tipo === 'volumen') {
    if (Q.length < 3 || !extra.cima) return null;
    const h = altoVolumen(m, Q, extra.cima);
    if (h == null) return null;
    const f = (FORMAS[extra.forma] || FORMAS.pila).factor;
    return areaPoligono(Q) * Math.abs(h) * f;
  }
  return null;
}

/** un tramo recto del mundo dibujado en la foto, siguiendo la curva del lente */
export function curva3D(m, A, B, n = 24) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const q = proyectar(m, [A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, (A[2] || 0) + ((B[2] || 0) - (A[2] || 0)) * t]);
    if (q) out.push(q);
  }
  return out.length >= 2 ? out : null;
}

/** dibujo de una medida en la foto: { lineas, verticales (guías finas), etiqueta (dónde va el número) } */
export function dibujoMedida(m, tipo, pts, extra = {}) {
  const lineas = [];
  const verticales = [];
  let etiqueta = null;
  if (tipo === 'altura') {
    if (pts.length < 2) return null;
    const X = alPiso(m, pts[0]);
    const z = alturaSobre(m, pts[0], pts[1]);
    if (!X || z == null) return null;
    const c = curva3D(m, [X[0], X[1], 0], [X[0], X[1], z]);
    if (c) { lineas.push(c); etiqueta = c[Math.floor(c.length / 2)]; }
    return { lineas, verticales, etiqueta };
  }
  const Q = puntos3D(m, tipo, pts, extra);
  if (!Q) return null;
  if (tipo === 'pared') {
    const A = alPiso(m, extra.linea[0]);
    const B = alPiso(m, extra.linea[1]);
    const c = curva3D(m, [A[0], A[1], 0], [B[0], B[1], 0]);
    if (c) verticales.push(c);
  }
  const cerrar = tipo === 'area' || tipo === 'volumen';
  const n = cerrar && Q.length >= 3 ? Q.length : Q.length - 1;
  for (let i = 0; i < n; i++) { const c = curva3D(m, Q[i], Q[(i + 1) % Q.length]); if (c) lineas.push(c); }
  if (tipo === 'distancia') (extra.bases || []).forEach((b, i) => { if (b && Q[i]) { const c = curva3D(m, [Q[i][0], Q[i][1], 0], Q[i]); if (c) verticales.push(c); } });
  if (cerrar && Q.length >= 3) {
    const c = centroide(Q);
    if (tipo === 'volumen' && extra.cima) {
      const h = altoVolumen(m, Q, extra.cima);
      if (h != null) { const v = curva3D(m, [c[0], c[1], 0], [c[0], c[1], h]); if (v) verticales.push(v); }
    }
    etiqueta = proyectar(m, [c[0], c[1], 0]);
  } else if (lineas.length) {
    const u = lineas[lineas.length - 1];
    etiqueta = u[Math.floor(u.length / 2)];
  }
  return { lineas, verticales, etiqueta };
}

/** la curva de un tramo del piso entre dos clics (vista previa mientras se mueve el mouse) */
export function tramoPiso(m, a, b) {
  const A = alPiso(m, a);
  const B = alPiso(m, b);
  return A && B ? curva3D(m, [A[0], A[1], 0], [B[0], B[1], 0]) : null;
}

/** cuánto se aparta (grados) una plomada marcada de la vertical que da el modelo en ese lugar */
export function desvioPlomada(m, base, punta) {
  const X = alPiso(m, base);
  const z = X ? alturaSobre(m, base, punta) : null;
  if (z == null || Math.abs(z) < 1e-6) return null;
  const q = proyectar(m, [X[0], X[1], z]);
  if (!q) return null;
  const a = [punta[0] - base[0], punta[1] - base[1]];
  const b = [q[0] - base[0], q[1] - base[1]];
  const c = (a[0] * b[0] + a[1] * b[1]) / ((Math.hypot(...a) * Math.hypot(...b)) || 1);
  return (Math.acos(Math.min(1, Math.max(-1, c))) * 180) / Math.PI;
}

/** ¿el piso que marcaste está torcido? Con la cámara que da el rectángulo solo, cada cosa vertical que marcó el
 * cliente tendría que caer sobre la vertical del modelo. Si se aparta más de lo que explica el clic, el rectángulo
 * no estaba horizontal (o la "vertical" no lo es). Ojo: una pendiente que sube alejándose de la cámara casi no se
 * nota así; la que va de costado sí. Devuelve { desvios: [grados], pendiente, max } */
export function revisarPendiente(ajusteSinPlomadas, plomadas, w) {
  if (!ajusteSinPlomadas?.ok || !plomadas?.length) return null;
  const desvios = plomadas.map((v) => {
    const d = desvioPlomada(ajusteSinPlomadas.modelo, v.base, v.punta);
    const L = Math.hypot(v.punta[0] - v.base[0], v.punta[1] - v.base[1]) || 1;
    const ruido = (3 * Math.atan((Math.SQRT2 * Math.max(sigmaBase(w), Number(v.punta[2]) || 0)) / L) * 180) / Math.PI;
    return { grados: d, tope: Math.max(UMBRAL_PENDIENTE, ruido) };
  });
  const malas = desvios.filter((d) => d.grados != null && d.grados > d.tope);
  return { desvios, pendiente: malas.length > 0 && malas.length >= Math.ceil(plomadas.length / 2), max: Math.max(0, ...desvios.map((d) => d.grados || 0)) };
}

// ---------- calibración ----------
export const sigmaBase = (w) => SIGMA_4K * (w / 3840);
const sig = (cal, p) => Math.max(sigmaBase(cal.imagen.w), Number(p[2]) || 0);
const tol = (ref) => (ref && ref.exacta === false ? TOL_APROX : TOL_EXACTA);

// Homografía mundo(X,Y) -> imagen con 4 puntos (h33 = 1), coordenadas normalizadas.
export function homografia4(world, img) {
  const s = 1 / Math.max(1, ...img.map((p) => Math.max(Math.abs(p[0]), Math.abs(p[1]))));
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i++) {
    const [X, Y] = world[i];
    const u = img[i][0] * s;
    const v = img[i][1] * s;
    A.push([X, Y, 1, 0, 0, 0, -u * X, -u * Y]);
    b.push(u);
    A.push([0, 0, 0, X, Y, 1, -v * X, -v * Y]);
    b.push(v);
  }
  const h = solve(A, b);
  if (!h) return null;
  return [[h[0] / s, h[1] / s, h[2] / s], [h[3] / s, h[4] / s, h[5] / s], [h[6], h[7], 1]];
}

/** focal desde la homografía del piso (ortogonalidad de r1, r2 e igual norma), null si no se puede */
export function focalDesdeH(H, cx, cy) {
  const col = (j) => [H[0][j] - cx * H[2][j], H[1][j] - cy * H[2][j], H[2][j]];
  const h1 = col(0);
  const h2 = col(1);
  const a1 = h1[0] * h2[0] + h1[1] * h2[1];
  const b1 = h1[2] * h2[2];
  const a2 = h1[0] * h1[0] + h1[1] * h1[1] - h2[0] * h2[0] - h2[1] * h2[1];
  const b2 = h1[2] * h1[2] - h2[2] * h2[2];
  const den = a1 * a1 + a2 * a2;
  if (den < 1e-30) return null;
  const u = -(a1 * b1 + a2 * b2) / den;
  return u > 0 ? 1 / Math.sqrt(u) : null;
}

/** pose inicial desde homografía + focal */
export function poseDesdeH(H, f, cx, cy) {
  const ki = (j) => [(H[0][j] - cx * H[2][j]) / f, (H[1][j] - cy * H[2][j]) / f, H[2][j]];
  let r1 = ki(0);
  let r2 = ki(1);
  let t = ki(2);
  const s = 2 / (norm(r1) + norm(r2));
  r1 = r1.map((x) => x * s);
  r2 = r2.map((x) => x * s);
  t = t.map((x) => x * s);
  if (t[2] < 0) {
    r1 = r1.map((x) => -x);
    r2 = r2.map((x) => -x);
    t = t.map((x) => -x);
  }
  const n1 = norm(r1);
  const a = r1.map((x) => x / n1);
  const d = dot(a, r2);
  let b = [r2[0] - d * a[0], r2[1] - d * a[1], r2[2] - d * a[2]];
  const nb = norm(b);
  b = b.map((x) => x / nb);
  const c = cross(a, b);
  const R = [[a[0], b[0], c[0]], [a[1], b[1], c[1]], [a[2], b[2], c[2]]];
  return { rv: rotToVec(R), t };
}

const mundoRect = (rect, signo) => [[0, 0], [rect.a, 0], [rect.a, signo * rect.b], [0, signo * rect.b]];

// Rectitud de una línea ya sin distorsión: distancias al ajuste por mínimos cuadrados totales.
function desvioLinea(pts) {
  const n = pts.length;
  let mx = 0;
  let my = 0;
  for (const p of pts) { mx += p[0]; my += p[1]; }
  mx /= n; my /= n;
  let sxx = 0; let syy = 0; let sxy = 0;
  for (const p of pts) { const x = p[0] - mx; const y = p[1] - my; sxx += x * x; syy += y * y; sxy += x * y; }
  const th = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  const nx = -Math.sin(th);
  const ny = Math.cos(th);
  return pts.map((p) => (p[0] - mx) * nx + (p[1] - my) * ny);
}

const paramsAModelo = (cal, p, conK) => crearModelo({
  w: cal.imagen.w, h: cal.imagen.h, f: cal.imagen.w * Math.exp(p[0]), rv: [p[1], p[2], p[3]], t: [p[4], p[5], p[6]], k: conK ? p[7] : 0,
});

function residuos(cal, mundoDe, p, conK) {
  const m = paramsAModelo(cal, p, conK);
  const mundo = mundoDe(p);
  if (conK && (m.k < -0.9 || m.k > 0.6)) return null;
  const r = [];
  const { rect } = cal;
  for (let i = 0; i < 4; i++) {
    const q = proyectar(m, [mundo[i][0], mundo[i][1], 0]);
    if (!q) return null;
    const s = sig(cal, rect.pts[i]);
    r.push((q[0] - rect.pts[i][0]) / s, (q[1] - rect.pts[i][1]) / s);
  }
  for (const sg of cal.segmentos || []) {
    const A = alPiso(m, sg.pts[0]);
    const B = alPiso(m, sg.pts[1]);
    if (!A || !B) return null;
    const L = Math.hypot(A[0] - B[0], A[1] - B[1]);
    r.push((L - sg.largo) / (sg.largo * tol(sg) + 0.01));
  }
  for (const v of cal.verticales || []) {
    const X = alPiso(m, v.base);
    if (!X) return null;
    const q = proyectar(m, [X[0], X[1], v.alto]);
    if (!q) return null;
    const pxLen = Math.hypot(v.punta[0] - v.base[0], v.punta[1] - v.base[1]);
    const s = Math.hypot(sig(cal, v.punta), sig(cal, v.base), pxLen * tol(v));
    r.push((q[0] - v.punta[0]) / s, (q[1] - v.punta[1]) / s);
  }
  // plomadas: cosas verticales (postes, esquinas) sin alto conocido. La punta tiene que caer sobre la vertical que
  // sale de la base (curvada por el lente): ayudan a fijar el giro y la inclinación de la cámara.
  for (const v of cal.plomadas || []) {
    const X = alPiso(m, v.base);
    if (!X) return null;
    const z = alturaSobre(m, v.base, v.punta);
    const q = z == null ? null : proyectar(m, [X[0], X[1], z]);
    if (!q) return null;
    // σ doble: una cosa vertical real nunca es perfecta (postes torcidos, esquinas con molduras)
    const s = 2 * Math.hypot(sig(cal, v.punta), sig(cal, v.base));
    r.push((q[0] - v.punta[0]) / s, (q[1] - v.punta[1]) / s);
  }
  if (conK) {
    for (const l of cal.lineas || []) {
      const und = l.pts.map((pt) => sinDistorsion(m, pt));
      if (und.some((x) => !x)) return null;
      const d = desvioLinea(und);
      d.forEach((x, i) => {
        const dx = (l.pts[i][0] - m.cx) / m.rd;
        const dy = (l.pts[i][1] - m.cy) / m.rd;
        r.push((x * (1 + m.k * (dx * dx + dy * dy))) / sig(cal, l.pts[i]));
      });
    }
    // previa de la curva del lente: el perfil de las cámaras (LENTE_REOLINK) o, sin eso, una muy débil hacia 0
    // (sólo las líneas marcadas a mano la fijan)
    const L = cal.lente;
    r.push(L && Number.isFinite(L.k) && L.sigma > 0 ? (p[7] - L.k) / L.sigma : p[7] / 0.5);
  }
  r.push(Math.log(Math.exp(p[0]) * cal.imagen.w / focalPorDefecto(cal.imagen.w)) / SIGMA_FOCAL); // previa de la focal: gran angular típico de obra
  // previa del giro: la vertical del mundo, vista en la foto, apunta hacia "arriba" (se anula si mira derecho al piso)
  const ar = cal.arriba || [0, -1];
  r.push((ar[0] * m.R[1][2] - ar[1] * m.R[0][2]) / SIGMA_ROLL);
  return r;
}

const costo = (r) => (r ? r.reduce((a, x) => a + x * x, 0) : Infinity);

export function levenberg(fun, p0, { maxIter = 80, tolRel = 1e-10 } = {}) {
  let p = p0.slice();
  let r = fun(p);
  let c = costo(r);
  if (!Number.isFinite(c)) return { p, c, r, ok: false };
  let mu = 1e-3;
  const n = p.length;
  for (let it = 0; it < maxIter; it++) {
    const J = [];
    for (let j = 0; j < n; j++) {
      const hstep = 1e-6 * Math.max(1, Math.abs(p[j]));
      const q = p.slice();
      q[j] += hstep;
      const rq = fun(q);
      if (!rq) {
        q[j] = p[j] - hstep;
        const rm = fun(q);
        if (!rm) return { p, c, r, ok: true };
        J.push(rm.map((x, i) => (r[i] - x) / hstep));
      } else J.push(rq.map((x, i) => (x - r[i]) / hstep));
    }
    const A = Array.from({ length: n }, (_, i) => Array.from({ length: n }, (_, j) => {
      let s = 0;
      for (let k = 0; k < r.length; k++) s += J[i][k] * J[j][k];
      return s;
    }));
    const g = Array.from({ length: n }, (_, i) => {
      let s = 0;
      for (let k = 0; k < r.length; k++) s += J[i][k] * r[k];
      return -s;
    });
    let mejoro = false;
    while (mu < 1e12) {
      const Ad = A.map((row, i) => row.map((x, j) => (i === j ? x + mu * (x + 1e-9) : x)));
      const d = solve(Ad, g);
      if (!d) { mu *= 10; continue; }
      const q = p.map((x, i) => x + d[i]);
      const rq = fun(q);
      const cq = costo(rq);
      if (cq < c) {
        const rel = (c - cq) / Math.max(c, 1e-30);
        p = q; r = rq; c = cq;
        mu = Math.max(mu / 3, 1e-12);
        mejoro = true;
        if (rel < tolRel) return { p, c, r, ok: true };
        break;
      }
      mu *= 4;
    }
    if (!mejoro) break;
  }
  return { p, c, r, ok: true };
}

/** solo la distorsión, con las líneas (para arrancar el ajuste) */
export function distorsionDesdeLineas(cal) {
  const lineas = (cal.lineas || []).filter((l) => l.pts.length >= 3);
  if (!lineas.length) return 0;
  const base = crearModelo({ w: cal.imagen.w, h: cal.imagen.h, f: 1, k: 0, rv: [0, 0, 0], t: [0, 0, 1] });
  const err = (k) => {
    const m = { ...base, k };
    let s = 0;
    for (const l of lineas) {
      const und = l.pts.map((pt) => sinDistorsion(m, pt));
      if (und.some((x) => !x)) return Infinity;
      const span = Math.hypot(und[0][0] - und[und.length - 1][0], und[0][1] - und[und.length - 1][1]) || 1;
      for (const d of desvioLinea(und)) s += (d / span) ** 2;
    }
    return s;
  };
  let lo = -0.85;
  let hi = 0.5;
  for (let i = 0; i < 60; i++) {
    const a = lo + (hi - lo) * 0.382;
    const b = lo + (hi - lo) * 0.618;
    if (err(a) < err(b)) hi = b; else lo = a;
  }
  return (lo + hi) / 2;
}

/** las 4 esquinas en el orden en que se recorre el rectángulo, empezando por la primera que se tocó (así el
 * cliente las puede tocar en cualquier orden) */
export function ordenarEsquinas(pts) {
  if (!pts || pts.length !== 4) return pts;
  const cx = pts.reduce((a, p) => a + p[0], 0) / 4;
  const cy = pts.reduce((a, p) => a + p[1], 0) / 4;
  const ang = (p) => Math.atan2(p[1] - cy, p[0] - cx);
  const orden = pts.map((p, i) => ({ p, i, a: ang(p) })).sort((x, y) => x.a - y.a);
  const k = orden.findIndex((x) => x.i === 0);
  return [0, 1, 2, 3].map((j) => orden[(k + j) % 4].p);
}

/** mismo rectángulo empezando una esquina más adelante: el lado 1-2 pasa a ser el otro */
export const rotarEsquinas = (pts) => (pts && pts.length === 4 ? [pts[1], pts[2], pts[3], pts[0]] : pts);

const convexo = (pts) => {
  let signo = 0;
  for (let i = 0; i < 4; i++) {
    const a = pts[i]; const b = pts[(i + 1) % 4]; const c = pts[(i + 2) % 4];
    const z = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (Math.abs(z) < 1e-9) return false;
    if (!signo) signo = Math.sign(z); else if (Math.sign(z) !== signo) return false;
  }
  return true;
};

/**
 * Ajusta la cámara a las referencias. cal = { imagen:{w,h}, rect:{pts[4], a, b, exacta}, lineas?:[{pts}],
 * verticales?:[{base, punta, alto, exacta}], segmentos?:[{pts[2], largo, exacta}], lente?:{k, sigma} }
 * lente = previa de la curva del lente (normalmente LENTE_REOLINK).
 * Devuelve { ok, modelo, params, conK, signo, rms, error? }
 */
// La vertical tiene que "subir" en la foto: descarta la pose espejada (con referencias chicas el ajuste puede
// encontrar la cámara dada vuelta). `arriba` es hacia dónde queda arriba en la foto SIN rotar: [0,-1] normal,
// [0,1] si la cámara está montada al revés (orientation 180), [-1,0] para 90 y [1,0] para 270.
export const arribaPorRotacion = (rot) => ({ 90: [-1, 0], 180: [0, 1], 270: [1, 0] }[rot] || [0, -1]);
function derecha(cal, modelo, signo, bEst) {
  const arriba = cal.arriba || [0, -1];
  const b = bEst || cal.rect.b;
  const c = [cal.rect.a / 2, (signo * b) / 2, 0];
  const q0 = proyectar(modelo, c);
  const q1 = proyectar(modelo, [c[0], c[1], 1]);
  if (!q0 || !q1) return false;
  return (q1[0] - q0[0]) * arriba[0] + (q1[1] - q0[1]) * arriba[1] > 0;
}

export function calibrar(cal, { inicio = null, maxIter = 120 } = {}) {
  const { rect } = cal;
  if (!rect || !rect.pts || rect.pts.length !== 4) return { ok: false, error: 'Faltan las 4 esquinas del rectángulo' };
  // Lado B desconocido: se puede, si hay otra referencia (vertical o de piso) que fije la escala que falta.
  const bLibre = !(rect.b > 0) && ((cal.verticales || []).length > 0 || (cal.segmentos || []).length > 0);
  if (!(rect.a > 0)) return { ok: false, error: 'Falta la medida del lado A del rectángulo' };
  if (!(rect.b > 0) && !bLibre) return { ok: false, error: 'Falta el lado B: si no lo sabés, marcá una referencia vertical de alto conocido' };
  if (!convexo(rect.pts)) return { ok: false, error: 'Las 4 esquinas se cruzan: marcalas en orden, dando la vuelta al rectángulo' };
  // con plomadas: primero el rectángulo solo, y desde ahí se afina con las plomadas (arrancar todo junto desde la
  // homografía se pierde con rectángulos chicos)
  if ((cal.plomadas || []).length && !inicio) {
    const sinP = calibrar({ ...cal, plomadas: [] }, { maxIter });
    if (!sinP.ok) return sinP;
    const conP = calibrar(cal, { inicio: sinP, maxIter });
    return conP.ok ? conP : { ...sinP, plomadasIgnoradas: true };
  }
  const conLineas = (cal.lineas || []).some((l) => l.pts.length >= 3);
  const conK = conLineas || !!(cal.lente && Number.isFinite(cal.lente.k));
  const { w } = cal.imagen;
  const nK = conK ? 8 : 7;
  const mundoDe = (signo) => (bLibre ? (p) => mundoRect({ a: rect.a, b: Math.exp(p[nK]) }, signo) : () => mundoRect(rect, signo));
  if (inicio) {
    const p0 = inicio.params.slice(0, nK);
    if (bLibre) p0.push(inicio.params[nK] ?? Math.log(rect.a));
    const fit = levenberg((p) => residuos(cal, mundoDe(inicio.signo), p, conK), p0, { maxIter });
    return armar(cal, fit, conK, inicio.signo, bLibre);
  }
  const k0 = conLineas ? distorsionDesdeLineas(cal) : conK ? cal.lente.k : 0;
  const tmp = crearModelo({ w, h: cal.imagen.h, f: 1, k: k0, rv: [0, 0, 0], t: [0, 0, 1] });
  const und = rect.pts.map((p) => sinDistorsion(tmp, p));
  if (und.some((x) => !x)) return { ok: false, error: 'No se pudo corregir la distorsión: revisá las líneas rectas' };
  let mejor = null;
  for (const signo of [1, -1]) for (const b0 of bLibre ? [0.5, 1, 2, 4, 8].map((x) => x * rect.a) : [rect.b]) {
    const mundo = mundoRect({ a: rect.a, b: b0 }, signo);
    const H = homografia4(mundo, und);
    if (!H) continue;
    let f = focalDesdeH(H, tmp.cx, tmp.cy);
    if (!f || f < 0.15 * w || f > 6 * w) f = focalPorDefecto(w);
    const fs = [f, ...[1, 0.6, 1.6, 2.6].map((x) => x * focalPorDefecto(w))];
    for (const f0 of fs) {
      const pose = poseDesdeH(H, f0, tmp.cx, tmp.cy);
      const m0 = crearModelo({ w, h: cal.imagen.h, f: f0, k: k0, rv: pose.rv, t: pose.t });
      if (centroCamara(m0)[2] <= 0) continue;
      const p0 = [Math.log(f0 / w), ...pose.rv, ...pose.t, ...(conK ? [k0] : []), ...(bLibre ? [Math.log(b0)] : [])];
      const fit = levenberg((p) => residuos(cal, mundoDe(signo), p, conK), p0, { maxIter });
      if (!fit.ok) continue;
      const m = paramsAModelo(cal, fit.p, conK);
      if (centroCamara(m)[2] <= 0) continue;
      if (!derecha(cal, m, signo, bLibre ? Math.exp(fit.p[nK]) : null)) continue;
      if (!mejor || fit.c < mejor.fit.c) mejor = { fit, signo };
    }
  }
  if (!mejor) return { ok: false, error: 'No pudimos ubicar el piso con esos puntos: revisá que el rectángulo esté sobre el piso y las medidas' };
  return armar(cal, mejor.fit, conK, mejor.signo, bLibre);
}

function armar(cal, fit, conK, signo, bLibre = false) {
  if (!fit.ok || !fit.r) return { ok: false, error: 'No pudimos ajustar la cámara con esas referencias' };
  const modelo = paramsAModelo(cal, fit.p, conK);
  if (centroCamara(modelo)[2] <= 0 || !derecha(cal, modelo, signo, bLibre ? Math.exp(fit.p[conK ? 8 : 7]) : null)) return { ok: false, error: 'Con esas referencias la cámara quedaría dada vuelta: revisá los puntos' };
  const n = fit.r.length - 2 - (conK ? 1 : 0); // sin las previas (focal, giro y lente)
  return { ok: true, costo: fit.c, modelo, params: fit.p, conK, signo, bEstimado: bLibre ? Math.exp(fit.p[conK ? 8 : 7]) : null, rms: Math.sqrt(fit.r.slice(0, n).reduce((a, x) => a + x * x, 0) / Math.max(1, n)) };
}

const mover = (r, p, s) => [p[0] + r.g() * s, p[1] + r.g() * s, p[2]];
const moverExtra = (r, extra, s0) => {
  if (!extra) return extra;
  const mv = (p) => (p ? mover(r, p, Math.max(s0, Number(p[2]) || 0)) : p);
  return { ...extra, ...(extra.bases ? { bases: extra.bases.map(mv) } : {}), ...(extra.linea ? { linea: extra.linea.map(mv) } : {}), ...(extra.cima ? { cima: mv(extra.cima) } : {}) };
};

/** calibraciones "vecinas" para el margen de error (Monte Carlo) */
export function muestras(cal, ajuste, n = MUESTRAS, seed = 7) {
  if (!ajuste?.ok) return [];
  const r = rng(seed);
  const out = [];
  for (let i = 0; i < n; i++) {
    const c2 = {
      ...cal,
      rect: {
        ...cal.rect,
        pts: cal.rect.pts.map((p) => mover(r, p, sig(cal, p))),
        a: cal.rect.a * (1 + r.g() * tol(cal.rect)),
        b: cal.rect.b * (1 + r.g() * tol(cal.rect)),
      },
      lineas: (cal.lineas || []).map((l) => ({ ...l, pts: l.pts.map((p) => mover(r, p, sig(cal, p))) })),
      verticales: (cal.verticales || []).map((v) => ({ ...v, base: mover(r, v.base, sig(cal, v.base)), punta: mover(r, v.punta, sig(cal, v.punta)), alto: v.alto * (1 + r.g() * tol(v)) })),
      segmentos: (cal.segmentos || []).map((s) => ({ ...s, pts: s.pts.map((p) => mover(r, p, sig(cal, p))), largo: s.largo * (1 + r.g() * tol(s)) })),
      plomadas: (cal.plomadas || []).map((v) => ({ base: mover(r, v.base, sig(cal, v.base)), punta: mover(r, v.punta, sig(cal, v.punta)) })),
      // la curva del lente estimada también tiene su incertidumbre: entra al ±
      ...(cal.lente && Number.isFinite(cal.lente.k) ? { lente: { ...cal.lente, k: cal.lente.k + r.g() * cal.lente.sigma } } : {}),
    };
    const a = calibrar(c2, { inicio: ajuste, maxIter: 25 });
    if (a.ok) out.push(a.modelo);
  }
  return out;
}

const cuantil = (xs, q) => {
  const s = xs.slice().sort((a, b) => a - b);
  const i = (s.length - 1) * q;
  const lo = Math.floor(i);
  return s[lo] + (s[Math.min(s.length - 1, lo + 1)] - s[lo]) * (i - lo);
};

/** medida + margen (intervalo del 95 %): { valor, err, errPct, inestable } */
export function medirConError(ajuste, vecinas, tipo, pts, { sigma, extra } = {}) {
  if (!ajuste?.ok) return { valor: null };
  const valor = medir(ajuste.modelo, tipo, pts, extra);
  if (valor == null || !Number.isFinite(valor)) return { valor: null };
  const s0 = sigma ?? sigmaBase(ajuste.modelo.w);
  const r = rng(hashPts([...pts, ...(extra?.cima ? [extra.cima] : []), ...(extra?.linea || [])]));
  const vals = [];
  for (const m of vecinas) {
    const v = medir(m, tipo, pts.map((p) => mover(r, p, Math.max(s0, Number(p[2]) || 0))), moverExtra(r, extra, s0));
    if (v != null && Number.isFinite(v)) vals.push(v);
  }
  if (vecinas.length && vals.length < vecinas.length * 0.8) return { valor, err: Infinity, errPct: Infinity, inestable: true };
  if (!vals.length) return { valor, err: null, errPct: null };
  let err = Math.max(Math.abs(cuantil(vals, 0.975) - valor), Math.abs(valor - cuantil(vals, 0.025)));
  // volumen: además, la duda de la forma (una montaña nunca es un cono perfecto)
  if (tipo === 'volumen') err = Math.hypot(err, 2 * (FORMAS[extra?.forma] || FORMAS.pila).duda * valor);
  return { valor, err, errPct: Math.abs(valor) > 1e-9 ? (100 * err) / Math.abs(valor) : null };
}

/** resumen para mostrar: precisión cerca/lejos, cámara estimada, cómo encajan las referencias extra, avisos */
export function calidad(cal, ajuste, vecinas) {
  if (!ajuste?.ok) return null;
  const m = ajuste.modelo;
  const C = centroCamara(m);
  const { rect } = cal;
  const cerca = medirConError(ajuste, vecinas, 'distancia', [rect.pts[0], rect.pts[2]]);
  // el mismo segmento, el doble de lejos de la cámara (si entra en la foto)
  const P = rect.pts.map((p) => alPiso(m, p));
  let lejos = null;
  if (P.every(Boolean)) {
    const cg = [(P[0][0] + P[2][0]) / 2, (P[0][1] + P[2][1]) / 2];
    const lejosC = [C[0] + 2 * (cg[0] - C[0]), C[1] + 2 * (cg[1] - C[1])];
    const d = [(P[2][0] - P[0][0]) / 2, (P[2][1] - P[0][1]) / 2];
    const a = proyectar(m, [lejosC[0] - d[0], lejosC[1] - d[1], 0]);
    const b = proyectar(m, [lejosC[0] + d[0], lejosC[1] + d[1], 0]);
    const dentro = (q) => q && q[0] >= 0 && q[1] >= 0 && q[0] <= m.w && q[1] <= m.h;
    if (dentro(a) && dentro(b)) lejos = medirConError(ajuste, vecinas, 'distancia', [a, b]);
  }
  const extras = [
    ...(cal.verticales || []).map((v, i) => {
      const h = alturaSobre(m, v.base, v.punta);
      return { tipo: 'vertical', i, real: v.alto, modelo: h, difPct: h == null ? null : (100 * (h - v.alto)) / v.alto };
    }),
    ...(cal.segmentos || []).map((s, i) => {
      const L = medir(m, 'distancia', s.pts);
      return { tipo: 'segmento', i, real: s.largo, modelo: L, difPct: L == null ? null : (100 * (L - s.largo)) / s.largo };
    }),
  ];
  const hfov = (2 * Math.atan(m.w / 2 / m.f) * 180) / Math.PI;
  const alturaVert = (cal.verticales || []).length
    ? medirConError(ajuste, vecinas, 'altura', [cal.verticales[0].base, cal.verticales[0].punta])
    : null;
  const avisos = [];
  if (ajuste.rms > 3) avisos.push('Las referencias no terminan de cerrar entre sí: revisá que los puntos estén bien marcados y que las medidas sean las reales.');
  if (hfov < 20 || hfov > 150) avisos.push(`La cámara daría un ángulo de visión raro (${Math.round(hfov)}°): revisá el rectángulo y sus medidas.`);
  if (C[2] < 1.5 || C[2] > 300) avisos.push(`La cámara quedaría a ${C[2].toFixed(1)} m del piso: revisá el rectángulo y sus medidas.`);
  if (m.k < -0.6) avisos.push('La foto tiene mucha distorsión: las medidas cerca de los bordes tienen más error.');
  for (const e of extras) if (e.difPct != null && Math.abs(e.difPct) > 8) avisos.push(`Una referencia ${e.tipo === 'vertical' ? 'vertical' : 'de piso'} no coincide (${e.difPct > 0 ? '+' : ''}${e.difPct.toFixed(0)} %): ¿está bien marcada y apoyada en el mismo piso?`);
  if (!vecinas.length) avisos.push('No pudimos estimar el margen de error.');
  return {
    precisionPct: cerca.errPct,
    lejosPct: lejos ? lejos.errPct : null,
    alturaPct: alturaVert ? alturaVert.errPct : null,
    alturaCamara: C[2],
    hfov,
    distorsion: m.k,
    rms: ajuste.rms,
    extras,
    avisos,
  };
}

// ---------- dibujo ----------
/** curva en la foto de un segmento recto del piso (sigue la distorsión), o null */
export function curvaPiso(m, A, B, n = 24) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const q = proyectar(m, [A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] ?? 0]);
    if (q) out.push(q);
  }
  return out.length >= 2 ? out : null;
}

/** grilla de piso alrededor del rectángulo (para ver si la calibración "apoya" bien) */
export function grillaPiso(m, rect, signo = 1) {
  const C = centroCamara(m);
  const P = rect.pts.map((p) => alPiso(m, p)).filter(Boolean);
  if (P.length < 4) return [];
  const cg = [P.reduce((a, p) => a + p[0], 0) / 4, P.reduce((a, p) => a + p[1], 0) / 4];
  const dist = Math.hypot(cg[0] - C[0], cg[1] - C[1]);
  const R = Math.max(dist * 1.6, Math.max(rect.a, rect.b) * 4);
  const pasos = [0.5, 1, 2, 5, 10, 20, 50];
  const paso = pasos.find((s) => (2 * R) / s <= 24) || 100;
  const ux = [(P[1][0] - P[0][0]), (P[1][1] - P[0][1])];
  const nu = Math.hypot(ux[0], ux[1]) || 1;
  const ex = [ux[0] / nu, ux[1] / nu];
  const ey = [-ex[1] * signo, ex[0] * signo];
  const o = P[0];
  const lineas = [];
  const N = Math.ceil(R / paso);
  const pt = (a, b) => [o[0] + ex[0] * a + ey[0] * b, o[1] + ex[1] * a + ey[1] * b, 0];
  for (let i = -N; i <= N; i++) {
    lineas.push(curvaPiso(m, pt(i * paso, -R), pt(i * paso, R), 40));
    lineas.push(curvaPiso(m, pt(-R, i * paso), pt(R, i * paso), 40));
  }
  return { paso, lineas: lineas.filter(Boolean) };
}

/** baldosas de 1 m sobre el piso, alineadas con el rectángulo (para ver a ojo si la calibración "apoya"):
 * [{ pts (contorno en la foto), par (para el damero) }] */
export function baldosasPiso(m, rect, signo = 1, { paso = 1, margen = 3, max = 900 } = {}) {
  const P = rect.pts.map((p) => alPiso(m, p));
  if (P.some((x) => !x)) return [];
  const ux = [P[1][0] - P[0][0], P[1][1] - P[0][1]];
  const nu = Math.hypot(ux[0], ux[1]) || 1;
  const ex = [ux[0] / nu, ux[1] / nu];
  const ey = [-ex[1] * signo, ex[0] * signo];
  const o = P[0];
  const loc = P.map((q) => [(q[0] - o[0]) * ex[0] + (q[1] - o[1]) * ex[1], (q[0] - o[0]) * ey[0] + (q[1] - o[1]) * ey[1]]);
  let x0 = Math.floor(Math.min(...loc.map((q) => q[0])) - margen);
  let x1 = Math.ceil(Math.max(...loc.map((q) => q[0])) + margen);
  let y0 = Math.floor(Math.min(...loc.map((q) => q[1])) - margen);
  let y1 = Math.ceil(Math.max(...loc.map((q) => q[1])) + margen);
  // con rectángulos grandes, se achica el borde para no pasarse de baldosas
  while ((x1 - x0) * (y1 - y0) / (paso * paso) > max && (x1 - x0 > 2 || y1 - y0 > 2)) { x0 += 1; x1 -= 1; y0 += 1; y1 -= 1; }
  const W = (a, b) => [o[0] + ex[0] * a + ey[0] * b, o[1] + ex[1] * a + ey[1] * b, 0];
  const out = [];
  for (let i = x0; i < x1; i += paso) {
    for (let j = y0; j < y1; j += paso) {
      const lados = [curvaPiso(m, W(i, j), W(i + paso, j), 4), curvaPiso(m, W(i + paso, j), W(i + paso, j + paso), 4), curvaPiso(m, W(i + paso, j + paso), W(i, j + paso), 4), curvaPiso(m, W(i, j + paso), W(i, j), 4)];
      if (lados.some((l) => !l || l.length < 5)) continue;
      const pts = lados.flatMap((l) => l.slice(0, -1));
      if (pts.some((q) => q[0] < -m.w * 0.2 || q[1] < -m.h * 0.2 || q[0] > m.w * 1.2 || q[1] > m.h * 1.2)) continue;
      out.push({ pts, par: (Math.round(i / paso) + Math.round(j / paso)) % 2 === 0 });
    }
  }
  return out;
}

/** guía vertical desde una base del piso (para marcar la punta de una altura) */
export function guiaVertical(m, base, hasta = 60) {
  const X = alPiso(m, base);
  if (!X) return null;
  const out = [];
  for (let i = 0; i <= 30; i++) {
    const q = proyectar(m, [X[0], X[1], (hasta * i) / 30]);
    if (q) out.push(q);
  }
  return out.length >= 2 ? out : null;
}

// ---------- formato ----------
export const fmtNum = (x, dec = 2) => (x == null || !Number.isFinite(x) ? '—' : x.toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
export const decimales = (x) => (Math.abs(x) >= 100 ? 0 : Math.abs(x) >= 10 ? 1 : 2);
export function fmtMedida(tipo, valor, err) {
  if (valor == null) return '—';
  const u = TIPOS[tipo]?.unidad || 'm';
  const d = decimales(valor);
  const e = err == null ? '' : !Number.isFinite(err) ? ' (margen muy grande)' : ` ±${fmtNum(err, d)}`;
  return `${fmtNum(valor, d)}${e} ${u}`;
}
